import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Page, StatusBadge } from "@/components/Shell";
import { SourceList } from "@/components/TaskCards";
import { api, errMsg, fmtTime } from "@/lib/api";

export default function TaskDetail() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => api.get(`/tasks/${id}`).then((r) => setT(r.data)).catch((e) => setErr(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);
  const active = t && ["queued", "running"].includes(t.status);
  useEffect(() => { if (!active) return; const h = setInterval(load, 2500); return () => clearInterval(h); }, [active, load]);

  const act = async (verb) => { try { await api.post(`/tasks/${id}/${verb}`); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (err) return <Page eyebrow="Task" title="Not found" intro={err} />;
  if (!t) return <Page eyebrow="Task" title="…" />;
  return (
    <Page eyebrow={`Research task · attempt ${t.attempts}/${t.max_attempts}`} title={t.title}
      action={<div className="flex items-center gap-4"><StatusBadge status={t.status} testId="task-detail-status" />
        {active && <button onClick={() => act("cancel")} data-testid="task-detail-cancel" className="px-5 py-2.5 rounded-full border border-[#2A2A2A] text-xs tracking-[0.18em] uppercase text-[#C0C0C0] hover:border-[#e2a6a6]/60 hover:text-[#e2a6a6] transition-colors">Cancel</button>}
        {["failed", "cancelled"].includes(t.status) && <button onClick={() => act("retry")} data-testid="task-detail-retry" className="px-5 py-2.5 rounded-full border border-[#2A2A2A] text-xs tracking-[0.18em] uppercase text-[#C0C0C0] hover:text-ivory transition-colors">Retry</button>}
      </div>}>
      <div className="grid md:grid-cols-12 gap-12 md:gap-16">
        <section className="md:col-span-7 space-y-14">
          {t.result?.summary && (
            <div data-testid="task-summary">
              <p className="eyebrow mb-5">Grounded summary</p>
              <p className="text-ivory font-light leading-relaxed whitespace-pre-wrap text-[1.02rem]">{t.result.summary}</p>
              <p className="mt-5 text-xs text-[#5f5f5f]" data-testid="task-verification">{t.result.verification?.grounded ? "Citations verified against retrieved sources." : "Citation verification did not fully pass."}</p>
            </div>
          )}
          {t.error && <p className="text-[#e2a6a6]" data-testid="task-detail-error">{t.error}</p>}
          {t.result?.sources && <div><p className="eyebrow mb-5">Sources</p><SourceList sources={t.result.sources} /></div>}
          {t.conversation_id && <Link to={`/c/${t.conversation_id}`} className="eyebrow !text-[#D8BFD8] link-underline" data-testid="task-open-conversation">Open conversation</Link>}
        </section>
        <aside className="md:col-span-5 space-y-12">
          <div>
            <p className="eyebrow mb-5">Steps</p>
            <ol className="border-l border-[#1c1c1c]" data-testid="task-steps">
              {t.steps.map((s) => (
                <li key={s.key} className="pl-6 pb-6 relative">
                  <span className={`absolute -left-[4px] top-1.5 h-2 w-2 rounded-full ${s.status === "done" ? "bg-[#C0C0C0]" : s.status === "running" ? "bg-[#D8BFD8] animate-pulse" : s.status === "failed" ? "bg-[#e2a6a6]" : "bg-[#2A2A2A]"}`} />
                  <p className="text-sm text-ivory">{s.label} <span className="ml-2 text-[0.6rem] tracking-[0.2em] uppercase text-[#5f5f5f]">{s.status}</span></p>
                  {s.detail && <p className="text-xs text-[#8A8A8A] mt-1 leading-relaxed">{s.detail}</p>}
                </li>
              ))}
            </ol>
          </div>
          <div>
            <p className="eyebrow mb-5">Log</p>
            <ul className="space-y-2 font-mono text-[0.7rem] text-[#8A8A8A] max-h-80 overflow-y-auto scrollbar-thin" data-testid="task-logs">
              {t.logs.map((l, i) => <li key={i}><span className="text-[#4a4a4a]">{fmtTime(l.at)}</span> <span className={l.level === "error" ? "text-[#e2a6a6]" : l.level === "warn" ? "text-[#e8d5a8]" : ""}>{l.text}</span></li>)}
            </ul>
          </div>
        </aside>
      </div>
    </Page>
  );
}
